import React, { useState } from "react";
import {
  Dialog,
  Slide,
  Grid,
  CardMedia,
  Typography,
  TextField as TF,
  Button,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { size, COLORS } from "../../utils";
import space from "../../assets/lottiefiles/space.json";
import Lottie from "react-lottie";

const useStyles = makeStyles(() => ({
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      borderWidth: size({ p: 0 }),
    },
  },
  cssFocused: {
    borderWidth: size({ p: 0 }),
  },
  notchedOutline: {
    borderWidth: size({ p: 0 }),
  },
}));

function TextField({ label, tf_props }) {
  const classes = useStyles();
  return (
    <Grid item container direction="column">
      <Grid item>
        <Typography
          sx={{
            fontFamily: "Space Mono",
            fontSize: size({ p: 1 }),
            fontWeight: 400,
            wordSpacing: size({ p: 0.2, neg: true }),
            marginBottom: size({ p: 0.5 }),
            color: `${COLORS.WHITE}A0`,
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <TF
          placeholder={label}
          {...tf_props}
          InputProps={{
            style: {
              fontFamily: "Space Mono",
              color: COLORS.WHITE,
              backgroundColor: "#242424",
              fontSize: size({ p: 1 }),
            },
            classes: {
              root: classes.cssOutlinedInput,
              focused: classes.cssFocused,
              notchedOutline: classes.notchedOutline,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default function Enroll(props) {
  const { onClose, open, data } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({ name: "", contact: "" });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setStatus("");

    const payload = {
      name: formData.name,
      contact: formData.contact,
      subject: "New Course Enrollment",
      body: `Name: ${formData.name}\nContact: ${formData.contact}`,
    };

    try {
      const res = await fetch(
        "http://api.thinkgrail.com/genesysemailapi/send-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();

      if (res.ok) {
        setStatus("✅ Thank you! We'll be in touch shortly.");
        setFormData({ name: "", contact: "" });
      } else {
        setStatus(result.error || "Submission failed. Try again.");
      }
    } catch (err) {
      console.error(err);
      setStatus("Something went wrong. Please try again later.");
    }

    setLoading(false);
  };

  return (
    <Dialog open={open} TransitionComponent={Slide} fullScreen>
      <div
        style={{
          width: isMobile ? "100%" : size({ p: 100 }),
          height: isMobile ? "100%" : size({ p: 100, h: true }),
          backgroundColor: COLORS.WHITE,
          backgroundImage: `
                    linear-gradient(${COLORS.C5}40 ${size({
            p: 0.06,
          })}, transparent ${size({ p: 0.06 })}),
                    linear-gradient(to right, ${COLORS.C5}40 ${size({
            p: 0.06,
          })}, ${COLORS.WHITE} ${size({ p: 0.06 })})
                `,
          backgroundSize: `${size({ p: 2.5 })} ${size({ p: 2 })}`,
          position: "relative",
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "column" : undefined,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Lottie Animation */}
        <div
          style={{
            position: "absolute",
            left: isMobile ? "1%" : size({ p: 50 }),
            top: isMobile ? "5%" : undefined,
          }}
        >
          <Lottie
            width={isMobile ? size({ p: 60 }) : size({ p: 40 })}
            height={isMobile ? size({ p: 60 }) : size({ p: 40 })}
            options={{
              loop: true,
              autoplay: true,
              animationData: space,
            }}
            style={isMobile ? { marginLeft: size({ p: 20 }) } : {}}
          />
        </div>

        {/* Form Container */}
        <div
          style={{
            marginLeft: isMobile ? 0 : size({ p: -38 }),
            marginTop: isMobile ? size({ p: 25 }) : 0,
            position: "relative",
          }}
        >
          <Grid
            item
            container
            direction="column"
            alignItems="center"
            justifyContent="start"
            flexWrap="nowrap"
            rowGap={size({ p: 1.5 })}
            sx={{
              backgroundColor: COLORS.C5,
              borderRadius: isMobile ? size({ p: 1.5 }) : size({ p: 0.6 }),
              width: isMobile ? size({ p: 85 }) : size({ p: 38 }),
              height: isMobile ? "auto" : size({ p: 74, h: true }),
              padding: isMobile ? size({ p: 6 }) : 0,
            }}
          >
            {/* Close Button */}
            <Grid item>
              <Typography
                onClick={onClose}
                sx={{
                  fontFamily: "Space Mono",
                  fontSize: isMobile ? size({ p: 5 }) : size({ p: 3 }),
                  color: COLORS.WHITE,
                  position: "absolute",
                  cursor: "pointer",
                  right: size({ p: 2 }),
                  top: size({ p: 1 }),
                }}
              >
                x
              </Typography>
            </Grid>

            {/* Header */}
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              rowGap={size({ p: 0.5 })}
            >
              <Grid item>
                <CardMedia
                  component="img"
                  image={data.icon}
                  sx={{
                    width: isMobile ? size({ p: 10 }) : size({ p: 7 }),
                    height: isMobile ? size({ p: 10 }) : size({ p: 7 }),
                  }}
                />
              </Grid>
              <Grid item>
                <Typography
                  sx={{
                    fontFamily: "Archia",
                    fontSize: isMobile ? size({ p: 2 }) : size({ p: 0.9 }),
                    fontWeight: 400,
                    color: `${COLORS.WHITE}A0`,
                    textAlign: "center",
                  }}
                >
                  Enroll Your Details
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Archia",
                    fontSize: isMobile ? size({ p: 3.8 }) : size({ p: 1.8 }),
                    fontWeight: 600,
                    color: COLORS.WHITE,
                    textAlign: "center",
                  }}
                >
                  {data.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Archia",
                    fontSize: isMobile ? size({ p: 2 }) : size({ p: 0.9 }),
                    fontWeight: 400,
                    color: `${COLORS.WHITE}A0`,
                    textAlign: "center",
                  }}
                >
                  8000+ Students Certified
                </Typography>
              </Grid>
            </Grid>

            {/* Form Fields */}
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              rowGap={size({ p: 1 })}
            >
              <Grid
                item
                sx={{ width: isMobile ? size({ p: 70 }) : size({ p: 24 }) }}
              >
                <TextField
                  tf_props={{
                    placeholder: "Name",
                    name: "name",
                    value: formData.name,
                    onChange: handleChange,
                    fullWidth: true,
                    required: true,
                  }}
                />
              </Grid>
              <Grid
                item
                sx={{ width: isMobile ? size({ p: 70 }) : size({ p: 24 }) }}
              >
                <TextField
                  tf_props={{
                    placeholder: "Contact Number",
                    name: "contact",
                    value: formData.contact,
                    onChange: handleChange,
                    fullWidth: true,
                    required: true,
                  }}
                />
              </Grid>
            </Grid>

            {/* Submit Button & Status */}
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              rowGap={size({ p: 0.3 })}
            >
              <Grid
                item
                sx={{ width: isMobile ? size({ p: 60 }) : size({ p: 24 }) }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    backgroundColor: COLORS.C6,
                    fontFamily: "Archia",
                    fontWeight: 400,
                    fontSize: isMobile ? size({ p: 2.3 }) : size({ p: 1 }),
                    color: COLORS.WHITE,
                    textTransform: "none",
                    ":hover": { backgroundColor: COLORS.C6 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={isMobile ? 20 : 16}
                      color="inherit"
                    />
                  ) : (
                    "Enroll Now"
                  )}
                </Button>
              </Grid>
              {status && (
                <Grid
                  item
                  sx={{ width: isMobile ? size({ p: 60 }) : size({ p: 24 }) }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Space Mono",
                      fontSize: size({ p: 0.7 }),
                      fontWeight: 400,
                      color: `${COLORS.WHITE}A0`,
                      textAlign: "center",
                    }}
                  >
                    {status}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
    </Dialog>
  );
}
